import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { audioBus, type SoundScene } from "@/lib/audioBus";

/**
 * ShowroomAmbient — Atmos-INSPIRED per-page scene engine.
 * (NOT real Dolby Atmos — see audioBus.ts header. No Dolby branding in UI.)
 *
 *  • Maps each route to a `SoundScene` (homepage, products, product-focus,
 *    one per manufacturer, contact-silent for contact/legal).
 *  • Override per page: any element inside #app-root with
 *    `data-sound-scene="…"` wins (scoped, never a global stale query).
 *  • Audio only starts after the FIRST real user gesture (tracked globally
 *    by audioBus.isUnlocked / subscribeUnlock — survives route changes).
 *  • Pauses on tab hidden / window blur, resumes when visible + focused.
 *  • Only runs in "immersive" mode and respects prefers-reduced-motion.
 *
 * Pure side-effect — renders nothing.
 */

const SILENT_PATTERNS: RegExp[] = [
  /^\/[a-z]{2}\/(kontakt|contact|contatto|contacto)(\/|$)/,
  /^\/[a-z]{2}\/(impressum|imprint|note-legali|aviso-legal|colofon)(\/|$)/,
  /^\/[a-z]{2}\/(datenschutz|privacy|privacidad|privacy-policy)(\/|$)/,
  /^\/[a-z]{2}\/(agb|terms|condizioni|condiciones|voorwaarden)(\/|$)/,
  /^\/[a-z]{2}\/(cookies?|cookie-policy)(\/|$)/,
];

// Slugs come from the manufacturers table (verified in DB).
const MFR_SLUG_TO_SCENE: Record<string, SoundScene> = {
  "kdln":              "manufacturer-kdln",
  "kundalini":         "manufacturer-kdln",
  "quasar":            "manufacturer-quasar",
  "quasar-holland":    "manufacturer-quasar",
  "vistosi":           "manufacturer-vistosi",
  "nemo":              "manufacturer-nemo",
  "nemo-lighting":     "manufacturer-nemo",
  "a-emotional-light": "manufacturer-a-emotional",
  "aemotional-light":  "manufacturer-a-emotional",
  "vibieffe":          "manufacturer-vibieffe",
};

function sceneForRoute(pathname: string): SoundScene {
  if (SILENT_PATTERNS.some((re) => re.test(pathname))) return "contact-silent";
  if (/^\/[a-z]{2}\/?$/.test(pathname)) return "home-showroom";

  const mfrDetail = pathname.match(
    /^\/[a-z]{2}\/(?:hersteller|manufacturers|produttori|fabricantes|fabrikanten)\/([a-z0-9-]+)/i,
  );
  if (mfrDetail) {
    const slug = mfrDetail[1].toLowerCase();
    return MFR_SLUG_TO_SCENE[slug] ?? "product-focus";
  }

  if (/\/(produkte|products|prodotti|productos|producten)\//.test(pathname)) {
    return "product-focus";
  }
  if (/\/(sortiment|products|prodotti|productos|assortiment)/.test(pathname)) {
    return "products-gallery";
  }
  if (/\/(hersteller|manufacturers|produttori|fabricantes|fabrikanten)/.test(pathname)) {
    return "products-gallery";
  }
  return "neutral";
}

const ShowroomAmbient = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Resolve route mapping first; allow per-page override only inside the
    // active app root so a stale element elsewhere can't leak.
    const root = document.getElementById("app-root") ?? document.body;
    const overrideEl = root.querySelector("[data-sound-scene]");
    const override = overrideEl?.getAttribute("data-sound-scene") as SoundScene | null;
    const scene: SoundScene = override ?? sceneForRoute(pathname);

    if (scene === "contact-silent") {
      audioBus.stopAmbient();
      return;
    }

    let active = true;

    const shouldPlay = () =>
      active &&
      audioBus.isUnlocked() &&
      audioBus.isEnabled() &&
      audioBus.getMode() === "immersive" &&
      document.visibilityState === "visible" &&
      document.hasFocus();

    const sync = () => {
      if (shouldPlay()) audioBus.startScene(scene);
      else audioBus.stopAmbient();
    };

    // Initial attempt — works immediately if user already unlocked audio
    // on a previous route (no second gesture required).
    sync();

    window.addEventListener("focus", sync);
    window.addEventListener("blur", sync);
    document.addEventListener("visibilitychange", sync);
    const unsubscribeMode = audioBus.subscribe(sync);
    const unsubscribeUnlock = audioBus.subscribeUnlock(sync);

    return () => {
      active = false;
      audioBus.stopAmbient();
      window.removeEventListener("focus", sync);
      window.removeEventListener("blur", sync);
      document.removeEventListener("visibilitychange", sync);
      unsubscribeMode();
      unsubscribeUnlock();
    };
  }, [pathname]);

  return null;
};

export default ShowroomAmbient;
